/**
 * Admin: Product Catalog management page.
 *
 * CRUD for products used in dynamic WhatsApp Flows.
 * Route: /admin/products (gated by 'commerce' feature)
 */

import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/utils'
import {
  Package, Plus, Search, Edit, Trash2, CalendarDays, ArrowLeftRight,
} from 'lucide-react'
import { Spinner } from '@/components/ui'
import { apiClient } from '@/services/apiClient'
import { useFeature } from '@/hooks'
import type { Product } from '@/types/commerce'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { ProductCapacityDrawer } from '@/components/admin/ProductCapacityDrawer'
import { Switch } from '@/components/ui/Switch'
import { ProductFormDrawer } from './ProductFormDrawer'
import { ProductImageThumb } from './ProductImageThumb'

function transformProduct(p: Record<string, unknown>): Product {
  return {
    id: p.id as string,
    userId: p.user_id as string,
    personaId: p.persona_id as string | null,
    name: p.name as string,
    category: p.category as string,
    description: p.description as string | null,
    priceCents: p.price_cents as number | null,
    currency: (p.currency as string) || 'MXN',
    serves: p.serves as string | null,
    alias: p.alias as string | null,
    leadTimeDays: (p.lead_time_days as number) || 0,
    isAvailable: (p.is_available as boolean) ?? true,
    availabilityType: (p.availability_type as string) || 'immediate',
    imageUrl: p.image_url as string | null,
    imageMediaRef: (p.image_media_ref as string | null) ?? null,
    imageMimeType: (p.image_mime_type as string | null) ?? null,
    hasImage: Boolean(p.image_media_ref),
    sortOrder: (p.sort_order as number) || 0,
    tags: p.tags as string[] | null,
    attributes: (p.attributes as Product['attributes']) ?? null,
    options: p.options as Array<Record<string, unknown>> | null,
    isActive: (p.is_active as boolean) ?? true,
    inventoryTracked: (p.inventory_tracked as boolean) ?? false,
    defaultDailyCapacity: (p.default_daily_capacity as number | null) ?? null,
    inventoryUnit: (p.inventory_unit as string) || 'items',
    availabilityLookaheadDays: (p.availability_lookahead_days as number) ?? 30,
    createdAt: p.created_at as string | null,
    updatedAt: p.updated_at as string | null,
  }
}

function formatPrice(cents: number | null, currency = 'MXN') {
  if (cents === null) return 'Quote required'
  return `$${(cents / 100).toLocaleString('es-MX', { minimumFractionDigits: 0 })} ${currency}`
}

export function ProductCatalogPage() {
  const showPortability = useFeature('portability')
  const [products, setProducts] = useState<Product[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingProduct, setEditingProduct] = useState<Product | null>(null)
  const [capacityProductId, setCapacityProductId] = useState<string | null>(null)

  const fetchProducts = useCallback(async () => {
    setIsLoading(true)
    try {
      const res = await apiClient.fetch('/api/commerce/products')
      if (res.ok) {
        const data = await res.json()
        setProducts((data.items || []).map(transformProduct))
      }
    } catch {
      // silent
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => { fetchProducts() }, [fetchProducts])

  const categories = [...new Set(products.map(p => p.category))].sort()

  const filtered = products.filter(p => {
    if (categoryFilter && p.category !== categoryFilter) return false
    if (search) {
      const q = search.toLowerCase()
      return p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q)
    }
    return true
  })

  const openEdit = (p: Product) => {
    setEditingProduct(p)
    setShowForm(true)
  }

  const handleDelete = async (id: string) => {
    try {
      await apiClient.fetch(`/api/commerce/products/${id}`, { method: 'DELETE' })
      fetchProducts()
    } catch {
      // silent
    }
  }

  // note: closes over click-time p; rapid double-toggle before settle is accepted
  const toggleAvailability = async (p: Product) => {
    setProducts(prev => prev.map(x => x.id === p.id ? { ...x, isAvailable: !p.isAvailable } : x))
    try {
      const res = await apiClient.fetch(`/api/commerce/products/${p.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_available: !p.isAvailable }),
      })
      // apiClient.fetch resolves on any status — treat non-2xx as failure.
      if (!res.ok) throw new Error(`availability toggle failed: ${res.status}`)
    } catch {
      setProducts(prev => prev.map(x => x.id === p.id ? { ...x, isAvailable: p.isAvailable } : x))
    }
  }

  return (
    <PageFrame width="detail" className="space-y-4">
      <PageHeader
        title="Product Catalog"
        icon={Package}
        sticky
        subtitle={`${products.length} ${products.length === 1 ? 'product' : 'products'}`}
        actions={
          <>
            {showPortability && (
              <Link
                to="/admin/portability"
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-border text-foreground rounded-lg hover:bg-muted"
              >
                <ArrowLeftRight className="w-3.5 h-3.5" /> Export / Import
              </Link>
            )}
            <button onClick={() => { setEditingProduct(null); setShowForm(true) }}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90">
              <Plus className="w-3.5 h-3.5" /> Add Product
            </button>
          </>
        }
      >
        {/* Filters */}
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input type="text" placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-border bg-card" />
          </div>
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
            className="px-3 py-2 text-sm rounded-lg border border-border bg-card">
            <option value="">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </PageHeader>

      <div className="space-y-4">
      {/* Product list */}
      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : filtered.length === 0 ? (
        <p className="text-center py-8 text-muted-foreground">No products found</p>
      ) : (
        <div className="bg-card rounded-lg border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted text-left text-xs text-muted-foreground uppercase tracking-wider">
                <th className="w-20 px-4 py-3"></th>
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">Serves</th>
                <th className="px-4 py-3">Lead Time</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map(p => (
                <tr key={p.id} className="hover:bg-muted">
                  <td className="w-20 px-4 py-2"><ProductImageThumb productId={p.id} hasImage={p.hasImage} /></td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-foreground">{p.name}</p>
                    {p.description && <p className="text-xs text-muted-foreground truncate max-w-xs">{p.description}</p>}
                    {(() => {
                      // Key by category:value — the same string can appear in
                      // more than one attribute category (e.g. a keyword that
                      // repeats an allergen).
                      const chips = p.attributes
                        ? [
                            ...p.attributes.allergens.map(c => ({ key: `allergens:${c}`, label: c })),
                            ...p.attributes.dietary.map(c => ({ key: `dietary:${c}`, label: c })),
                            ...p.attributes.keywords.map(c => ({ key: `keywords:${c}`, label: c })),
                          ]
                        : []
                      if (chips.length === 0) return null
                      return (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {chips.slice(0, 4).map(c => (
                            <span key={c.key} className="px-1.5 py-0.5 text-[10px] rounded-full bg-muted text-muted-foreground">{c.label}</span>
                          ))}
                          {chips.length > 4 && (
                            <span className="text-[10px] text-muted-foreground">+{chips.length - 4}</span>
                          )}
                        </div>
                      )
                    })()}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground capitalize">{p.category}</td>
                  <td className="px-4 py-3">
                    <span className={cn(p.priceCents !== null ? 'text-foreground' : 'text-muted-foreground italic')}>
                      {formatPrice(p.priceCents, p.currency)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{p.serves || '—'}</td>
                  <td className="px-4 py-3 text-muted-foreground">{p.leadTimeDays}d</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Switch size="sm" checked={p.isAvailable}
                        onCheckedChange={() => toggleAvailability(p)}
                        aria-label={p.isAvailable ? `Disable ${p.name}` : `Enable ${p.name}`} />
                      <span className={cn('text-xs', p.isAvailable ? 'text-muted-foreground' : 'text-warning-strong')}>
                        {p.isAvailable ? 'Available' : 'Unavailable'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(p)} className="p-1 hover:bg-muted rounded-sm">
                        <Edit className="w-3.5 h-3.5 text-muted-foreground" />
                      </button>
                      {p.inventoryTracked && (
                        <button
                          onClick={() => setCapacityProductId(p.id)}
                          className="p-1 hover:bg-muted rounded-sm"
                          title="View daily capacity"
                        >
                          <CalendarDays className="w-3.5 h-3.5 text-primary" />
                        </button>
                      )}
                      <button onClick={() => handleDelete(p.id)} className="p-1 hover:bg-destructive/10 rounded-sm">
                        <Trash2 className="w-3.5 h-3.5 text-destructive" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ProductCapacityDrawer
        productId={capacityProductId!}
        open={!!capacityProductId}
        onClose={() => setCapacityProductId(null)}
      />

      <ProductFormDrawer
        open={showForm}
        product={editingProduct}
        onClose={() => { setShowForm(false); setEditingProduct(null) }}
        onSaved={() => { setShowForm(false); setEditingProduct(null); fetchProducts() }}
        onImageChanged={fetchProducts}
      />
      </div>
    </PageFrame>
  )
}
